import * as VideoProcessors from '@twilio/video-processors';
import {filter, find, forEach, map, size, some} from 'lodash';
import {useMemo, useContext, useCallback} from 'react';

import {SystemEndSessionCode} from 'constants/session';
import {UserSessionContext, UserContext} from 'context';
import {
    AsyncRequestStatusName, AudioVideoView, GuestParticipant, ScreenSharingActivityStatus, SessionActivityName, SessionGoal,
    SessionGoalType, SessionScreenshotSubject, SessionStatus, WebRTCSpeechActivityData
} from 'interfaces/session';
import {UserSessionResponsePayload} from 'pages/User/Conference/types';
import {getAssignmentName} from 'services';
import {DefaultParticipant, InviteeParticipantRole, ParticipantRole} from 'types';
import {getScreenSharingPresenterId, sessionUtils} from 'utils';

export function useUserSession(data?: {payload: UserSessionResponsePayload}) {
    const sessionContext = useContext(UserSessionContext);
    const userContext = useContext(UserContext);

    const {session, settings} = useMemo(() => data?.payload || sessionContext, [data, sessionContext]);
    const sessionAccessOnly = useMemo(() => userContext.auth.sessionAccessOnly, [userContext]);
    const isStarted = useMemo(() => some(session?.participants, (p) => p.role === ParticipantRole.Client), [session]);
    const isEnded = useMemo(() => session?.status === SessionStatus.SessionEnded, [session]);
    const isEndedBySystem = useMemo(() => isEnded && session?.result?.finishedBy === DefaultParticipant.System, [session, isEnded]);
    const sessionResultInformation = useMemo(() => session?.result?.information, [session]);
    const canManageSession = useMemo(() => session?.localParticipant?.role === ParticipantRole.Agent, [session]);

    const aiSuggestionsData = useMemo(() => ({
        aiSuggestionsEnabled: canManageSession && settings?.userSession?.aiSuggestions?.enabled,
        aiSuggestions: sessionContext.aiSuggestions,
        aiSuggestionsLoading: sessionContext.aiSuggestionsLoading,
        aiSuggestionsError: sessionContext.aiSuggestionsError
    }), [settings, sessionContext, canManageSession]);

    const aiSuggestionsAudioData = useMemo(() => ({
        aiSuggestionsAudioEnabled: settings?.userSession?.aiSuggestionsAudio?.enabled,
        aiSuggestionsAudio: sessionContext.aiSuggestionsAudio,
        aiSuggestionsAudioLoading: sessionContext.aiSuggestionsAudioLoading,
        aiSuggestionsAudioError: sessionContext.aiSuggestionsAudioError
    }), [settings, sessionContext]);

    const settingsData = useMemo(() => {
        const feedbackRatingEnabled = settings?.userSession?.feedback?.rating?.enabled;
        const feedbackCommentEnabled = settings?.userSession?.feedback?.comment?.enabled;
        const agentLeft = session?.result?.information === SystemEndSessionCode.NoActiveAgents && isEndedBySystem;
        const audioVideoView = size(session?.participants) > 1 ?
            AudioVideoView.Conference : settings?.session?.audioVideoDefaultView;

        return {
            canSwitchCamera: settings?.userSession?.canSwitchCamera,
            isOnHoldEnabled: settings?.session?.onHold?.enabled,
            subtitlesEnabled: settings?.userSession?.subtitles?.enabled,
            keepTextChatOnRestart: settings?.session?.keepTextChatOnRestart?.enabled,
            sessionQueueSettings: settings?.session?.queue,
            isScreenshotsPreviewEnabled: settings?.session?.screenshotsPreview?.enabled,
            canChangeParticipantDevicePermission: settings?.session?.participantDevicePermissionChange?.enabled,
            isReassignmentEnabled: settings?.session?.reassignment?.enabled,
            canChangeAssignment: settings?.session?.assignmentChange?.enabled &&
                session?.assignment?.groupId &&
                find(sessionContext?.groups, {id: session?.assignment?.groupId})?.subgroups?.length > 0,
            feedbackAvailability: {
                rating: feedbackRatingEnabled,
                comment: feedbackCommentEnabled,
            },
            canInviteGuest: settings?.session?.canInviteGuest,
            canInviteAssistant: settings?.session?.canInviteAssistant,
            canRemoveGuest: settings?.session?.canRemoveGuest,
            canRemoveAssistant: settings?.session?.canRemoveAssistant,
            maxGuests: settings?.session?.maxGuests,
            maxAssistants: settings?.session?.maxAssistants,
            maxParticipants: settings?.session?.maxParticipants,
            assistantEnabled: userContext?.settings?.featuresAvailability?.assistantEnabled,
            videoPinEnabled: settings?.userSession?.videoPin?.enabled,
            videoPinnedParticipant: settings?.userSession?.videoPin?.participant,
            canSendFeedback: isStarted && !agentLeft && (feedbackRatingEnabled || feedbackCommentEnabled),
            canUpdateParticipantData: settings?.userSession?.canUpdateParticipantData,
            isPictureInPictureEnabled: settings?.userSession?.pictureInPicture?.enabled,
            isTextChatTranslationEnabled: settings?.session?.textChatTranslation?.enabled,
            isAudioTranslationEnabled: settings?.session?.audioTranslation?.enabled,
            isAudioVideoTranslationEnabled: settings?.session?.audioVideoTranslation?.enabled,
            audioVideoView,
            audioVideoViewConference: audioVideoView === AudioVideoView.Conference,
            virtualBackgroundEnabled: VideoProcessors.isSupported && settings?.userSession?.virtualBackground?.enabled,
            guestEnabled: userContext?.settings?.featuresAvailability?.guestEnabled
        };
    }, [session, settings, sessionContext, isStarted, isEndedBySystem, userContext?.settings?.featuresAvailability?.guestEnabled,
        userContext?.settings?.featuresAvailability?.assistantEnabled]);

    const sessionData = useMemo(() => ({
        sessionId: session?.id,
        participants: session?.participants,
        participantId: session?.localParticipant?.id,
        participantIds: map(session?.participants, (p) => p.id),
        localParticipant: session?.localParticipant,
        localParticipantToken: session?.localParticipant?.token,
        userParticipantIdGroups: sessionUtils.getUserParticipantIdGroups(session),
        clientParticipant: sessionUtils.getClientParticipant(session),
        isMobileSdkSession: sessionUtils.isClientDeviceSDK(sessionUtils.getClientParticipant(session)?.data?.device),
        guestParticipants: sessionUtils.getGuestParticipants(session),
        activeGuestParticipants: filter(sessionUtils.getGuestParticipants(session), (p) => !(p as GuestParticipant).removed),
        assistantParticipants: filter(session?.participants, {role: ParticipantRole.Assistant}),
        activeAssistantParticipants: filter(session?.participants, (p) => p.role === ParticipantRole.Assistant && !p.removed),
        fetchedSessionVersion: session?.version,
        isStarted,
        isOnHold: session?.status === SessionStatus.OnHold,
        isInSession: session?.status === SessionStatus.InSession,
        isEnded,
        isEndedBySystem,
        sessionResultInformation,
        screenshots: session?.screenshots,
        receivedScreenshots: filter(session?.screenshots, {received: true}),
        assignmentName: getAssignmentName(sessionContext?.groups, session?.assignment?.groupId, session?.assignment?.subgroupId),
        reassigned: session?.reassigned,
        prevActivities: session?.prevActivities,
        canManageSession,
        canSeeCoBrowsing: canManageSession,
    }), [session, sessionContext.groups, isStarted, isEnded, isEndedBySystem, sessionResultInformation, canManageSession]);

    const getActivityByName = useCallback((name: SessionActivityName) => find(session?.activities, {name}), [session]);
    const getPrevActivityByName = useCallback((name: SessionActivityName) => find(session?.prevActivities, {name}), [session]);
    const getActivityById = useCallback((id: string) => find(session?.activities, {id}), [session]);

    const activitiesData = useMemo(() => {
        const audioVideoActivity = getActivityByName(SessionActivityName.AudioVideo);
        const audioActivity = getActivityByName(SessionActivityName.Audio);
        const textChatActivity = getActivityByName(SessionActivityName.TextChat);
        const screenSharingActivity = getActivityByName(SessionActivityName.ScreenSharing);
        const coBrowsingActivity = getActivityByName(SessionActivityName.CoBrowsing);
        const sipActivity = getActivityByName(SessionActivityName.SIP);
        const avatarActivity = getActivityByName(SessionActivityName.Avatar);
        const agentChatbotActivity = getActivityByName(SessionActivityName.AgentChatbot);
        const screenSharingActivityStatus: ScreenSharingActivityStatus = screenSharingActivity?.data?.status;
        const prevSipActivity = getPrevActivityByName(SessionActivityName.SIP);
        const screenSharingPresenterId = getScreenSharingPresenterId(session);
        const isScreenSharingPresenter = screenSharingPresenterId === session?.localParticipant?.id;

        return {
            audioVideoActivity,
            audioActivity,
            textChatActivity,
            screenSharingActivity,
            coBrowsingActivity,
            /**
             * @deprecated. Avatar activity ends when session is calling.
             */
            avatarActivity,
            agentChatbotActivity,
            sipActivity,
            prevSipActivity,
            webRTCActivities: filter([audioVideoActivity, audioActivity, screenSharingActivity]),
            screenSharingActivityStatus,
            screenSharingPresenterId,
            isScreenSharingPresenterLocal: isScreenSharingPresenter,
            videoMinimized: sessionUtils.getVideoMinimized(session),
            transcriptionLanguage: (audioActivity?.data || audioVideoActivity?.data)
                ?.translationSettings?.[session?.localParticipant?.id]?.language,
            audioVideoAsColumn: !!audioVideoActivity && (
                (screenSharingActivityStatus === ScreenSharingActivityStatus.Accepted && !isScreenSharingPresenter)
                || (!!coBrowsingActivity && sessionData?.canSeeCoBrowsing)
            ) && settingsData?.audioVideoViewConference
        };
    }, [session, settingsData, sessionData, getPrevActivityByName, getActivityByName]);

    const audioStreamEnabled = useMemo(() => {
        if (!settings?.userSession?.streamAudio?.enabled) {
            return false;
        }
        return !!(
            (activitiesData.audioActivity?.data || activitiesData.audioVideoActivity?.data) as WebRTCSpeechActivityData
        )?.streamAudio?.enabled;
    },
    [activitiesData, settings]);

    const sessionParams = useMemo(() => {
        const {sessionId, participantId, localParticipantToken, fetchedSessionVersion} = sessionData;
        return {
            sessionId, participantId,
            token: localParticipantToken,
            fv: fetchedSessionVersion
        };
    }, [sessionData]);

    const goals = useMemo(() => {
        const result: SessionGoal[] = [];
        const {participantId, participants, screenshots} = sessionData;
        forEach(screenshots, (screenshot) => {
            if (screenshot.required) {
                if (screenshot.subject === SessionScreenshotSubject.LocalScreen) {
                    result.push({
                        type: SessionGoalType.Screenshot,
                        participantId,
                        completed: screenshot.received,
                        data: {screenshotName: screenshot.name}
                    });
                } else if (screenshot.subject === SessionScreenshotSubject.ParticipantVideo) {
                    const filteredParticipants = filter(participants, (p) => {
                        if (screenshot.participantId) {
                            return screenshot.participantId === p.id;
                        }

                        if (p.role === ParticipantRole.Guest && !settingsData.guestEnabled) {
                            return false;
                        }

                        if (screenshot.role === InviteeParticipantRole.Guest) {
                            return !settingsData.guestEnabled || p.role === ParticipantRole.Guest;
                        }

                        return p.role === ParticipantRole.Client;
                    });

                    forEach(filteredParticipants, (participant) => {
                        result.push({
                            type: SessionGoalType.Screenshot,
                            participantId: participant.id,
                            completed: screenshot.received,
                            data: {screenshotName: screenshot.name}
                        });
                    });
                }
            }
        });

        forEach(participants, (participant) => {
            forEach(participant.asyncRequests, (request) => {
                if (request.required) {
                    result.push({
                        type: SessionGoalType.AsyncRequest,
                        participantId: participant.id,
                        completed: request.status.name === AsyncRequestStatusName.Success,
                        data: {asyncRequestType: request.type}
                    });
                }
            });
        });

        return result;
    }, [sessionData, settingsData]);

    return {
        session, settings, sessionParams, sessionAccessOnly, goals, getActivityByName, getActivityById, audioStreamEnabled,
        ...sessionData, ...settingsData, ...activitiesData, ...aiSuggestionsData, ...aiSuggestionsAudioData
    };
}
